import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import {
  ZoomIn,
  ZoomOut,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Trash2,
  Scissors,
} from "lucide-react";
import type { Subtitle } from "./subtitle-editor";

interface TimelineOverlay {
  type: string;
  startTime: number;
  endTime: number;
  payload: Record<string, unknown>;
}

interface EditingTimelineProps {
  duration: number;
  currentTime: number;
  onSeek: (time: number) => void;
  subtitles: Subtitle[];
  overlays: TimelineOverlay[];
  onSubtitleSelect?: (index: number) => void;
  onOverlaySelect?: (index: number) => void;
  onSubtitleTimingChange?: (index: number, startTime: number, endTime: number) => void;
  onOverlayTimingChange?: (index: number, startTime: number, endTime: number) => void;
  onSubtitleDelete?: (index: number) => void;
  onOverlayDelete?: (index: number) => void;
  onSubtitleSplit?: (index: number, splitTime: number) => void;
  onOverlaySplit?: (index: number, splitTime: number) => void;
  selectedSubtitleIndex?: number | null;
  selectedOverlayIndex?: number | null;
  fileName?: string;
}

// --- Constants ---

const TRACK_HEIGHT = 44;
const RULER_HEIGHT = 28;
const TRACK_LABEL_WIDTH = 120;
const MIN_ZOOM = 8;
const MAX_ZOOM = 120;
const DEFAULT_ZOOM = 20;
const DRAG_HANDLE_WIDTH = 6;
const SNAP_THRESHOLD_PX = 4;

const OVERLAY_COLORS: Record<string, string> = {
  "title-slide": "#8b5cf6",
  "stat-counter": "#06b6d4",
  "note-card": "#f59e0b",
  "floating-card": "#10b981",
  "comment-box": "#ec4899",
  "retention-chart": "#3b82f6",
  "comparison-rows": "#f97316",
  "circle-portrait": "#a855f7",
};

const OVERLAY_LABELS: Record<string, { name: string; icon: string }> = {
  "title-slide": { name: "Tytuł", icon: "🎬" },
  "stat-counter": { name: "Licznik", icon: "🔢" },
  "note-card": { name: "Notatka", icon: "📝" },
  "floating-card": { name: "Karta", icon: "💬" },
  "comment-box": { name: "Komentarz", icon: "💭" },
  "retention-chart": { name: "Wykres", icon: "📊" },
  "comparison-rows": { name: "Porównanie", icon: "📋" },
  "circle-portrait": { name: "Portret", icon: "⭕" },
};

const SUBTITLE_COLOR = "#38bdf8";
const VIDEO_TRACK_COLOR = "#22c55e";
const AUDIO_TRACK_COLOR = "#a78bfa";
const MEDIA_TRACK_HEIGHT = 36;

// --- Helpers ---

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${m}:${s.toString().padStart(2, "0")}.${ms}`;
}

function formatTimeShort(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function getOverlayText(overlay: TimelineOverlay): string {
  const p = overlay.payload;
  const textExtractors: Record<string, () => string> = {
    "title-slide": () => (p.headline as string) ?? "",
    "stat-counter": () => `${p.prefix ?? ""}${p.targetValue}${p.suffix ?? ""}`,
    "note-card": () => (p.title as string) ?? "",
    "floating-card": () => (p.text as string) ?? "",
    "comment-box": () => `@${p.username}: ${p.text}`,
    "retention-chart": () => (p.chartLabel as string) ?? "",
    "comparison-rows": () => (p.title as string) ?? "",
    "circle-portrait": () => (p.text as string) ?? "",
  };
  return textExtractors[overlay.type]?.() ?? overlay.type;
}

type DragState = {
  type: "subtitle" | "overlay";
  index: number;
  handle: "left" | "right" | "move";
  initialX: number;
  initialStart: number;
  initialEnd: number;
};

// --- Track groups ---

interface TrackGroup {
  id: string;
  label: string;
  icon: string;
  color: string;
  items: Array<{
    sourceType: "subtitle" | "overlay";
    sourceIndex: number;
    startTime: number;
    endTime: number;
    text: string;
  }>;
}

function buildTrackGroups(subtitles: Subtitle[], overlays: TimelineOverlay[]): TrackGroup[] {
  const groups: TrackGroup[] = [];

  // Subtitle track (always present)
  groups.push({
    id: "subtitles",
    label: "Napisy",
    icon: "📝",
    color: SUBTITLE_COLOR,
    items: subtitles.map((sub, i) => ({
      sourceType: "subtitle" as const,
      sourceIndex: i,
      startTime: sub.startTime,
      endTime: sub.endTime,
      text: sub.text,
    })),
  });

  // Group overlays by type
  const overlaysByType = new Map<string, Array<{ index: number; overlay: TimelineOverlay }>>();
  overlays.forEach((overlay, i) => {
    const existing = overlaysByType.get(overlay.type);
    if (existing) {
      existing.push({ index: i, overlay });
    } else {
      overlaysByType.set(overlay.type, [{ index: i, overlay }]);
    }
  });

  overlaysByType.forEach((items, type) => {
    const info = OVERLAY_LABELS[type] ?? { name: type, icon: "🎨" };
    const color = OVERLAY_COLORS[type] ?? "#8b5cf6";
    groups.push({
      id: `overlay-${type}`,
      label: info.name,
      icon: info.icon,
      color,
      items: items.map(({ index, overlay }) => ({
        sourceType: "overlay" as const,
        sourceIndex: index,
        startTime: overlay.startTime,
        endTime: overlay.endTime,
        text: getOverlayText(overlay),
      })),
    });
  });

  return groups;
}

// --- Component ---

export default function EditingTimeline({
  duration,
  currentTime,
  onSeek,
  subtitles,
  overlays,
  onSubtitleSelect,
  onOverlaySelect,
  onSubtitleTimingChange,
  onOverlayTimingChange,
  onSubtitleDelete,
  onOverlayDelete,
  onSubtitleSplit,
  onOverlaySplit,
  selectedSubtitleIndex,
  selectedOverlayIndex,
  fileName,
}: EditingTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [collapsedTracks, setCollapsedTracks] = useState<Set<string>>(new Set());
  const [hiddenTracks, setHiddenTracks] = useState<Set<string>>(new Set());
  const [dragState, setDragState] = useState<DragState | null>(null);

  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 1;
  const pxPerSecond = zoom;
  const totalWidth = Math.max(safeDuration * pxPerSecond, 300);

  const trackGroups = useMemo(() => buildTrackGroups(subtitles, overlays), [subtitles, overlays]);

  // Keep playhead visible
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || dragState) return;
    const playheadX = currentTime * pxPerSecond;
    const viewLeft = el.scrollLeft;
    const viewRight = viewLeft + el.clientWidth;
    if (playheadX < viewLeft + 30 || playheadX > viewRight - 30) {
      el.scrollLeft = Math.max(0, playheadX - el.clientWidth / 3);
    }
  }, [currentTime, pxPerSecond, dragState]);

  // --- Zoom ---

  const zoomIn = useCallback(() => setZoom((z) => Math.min(MAX_ZOOM, z * 1.4)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(MIN_ZOOM, z / 1.4)), []);

  // --- Track toggle ---

  const toggleCollapse = useCallback((trackId: string) => {
    setCollapsedTracks((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) next.delete(trackId);
      else next.add(trackId);
      return next;
    });
  }, []);

  const toggleVisibility = useCallback((trackId: string) => {
    setHiddenTracks((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) next.delete(trackId);
      else next.add(trackId);
      return next;
    });
  }, []);

  // --- Seeking (click on ruler/track background) ---

  const handleRulerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      if (!el) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left + el.scrollLeft;
      const time = Math.max(0, Math.min(safeDuration, x / pxPerSecond));
      onSeek(time);
    },
    [safeDuration, pxPerSecond, onSeek]
  );

  // --- Drag handlers ---

  const startDrag = useCallback(
    (
      e: React.MouseEvent,
      type: "subtitle" | "overlay",
      index: number,
      handle: "left" | "right" | "move",
      startTime: number,
      endTime: number
    ) => {
      e.stopPropagation();
      e.preventDefault();
      setDragState({
        type,
        index,
        handle,
        initialX: e.clientX,
        initialStart: startTime,
        initialEnd: endTime,
      });
    },
    []
  );

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragState.initialX;
      const deltaSec = deltaX / pxPerSecond;

      let newStart = dragState.initialStart;
      let newEnd = dragState.initialEnd;
      const minDuration = 0.2;

      if (dragState.handle === "left") {
        newStart = Math.max(0, dragState.initialStart + deltaSec);
        if (newStart > newEnd - minDuration) newStart = newEnd - minDuration;
      } else if (dragState.handle === "right") {
        newEnd = Math.min(safeDuration, dragState.initialEnd + deltaSec);
        if (newEnd < newStart + minDuration) newEnd = newStart + minDuration;
      } else {
        const dur = dragState.initialEnd - dragState.initialStart;
        newStart = Math.max(0, dragState.initialStart + deltaSec);
        newEnd = newStart + dur;
        if (newEnd > safeDuration) {
          newEnd = safeDuration;
          newStart = newEnd - dur;
        }
      }

      // Snap to nearest second
      const snapSec = (val: number) => {
        const rounded = Math.round(val);
        return Math.abs((val - rounded) * pxPerSecond) < SNAP_THRESHOLD_PX ? rounded : val;
      };

      if (dragState.handle !== "move") {
        newStart = snapSec(newStart);
        newEnd = snapSec(newEnd);
      }

      const callback =
        dragState.type === "subtitle" ? onSubtitleTimingChange : onOverlayTimingChange;
      callback?.(dragState.index, newStart, newEnd);
    };

    const handleMouseUp = () => {
      setDragState(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState, pxPerSecond, safeDuration, onSubtitleTimingChange, onOverlayTimingChange]);

  // --- Item click ---

  const handleItemClick = useCallback(
    (sourceType: "subtitle" | "overlay", sourceIndex: number) => {
      if (sourceType === "subtitle") onSubtitleSelect?.(sourceIndex);
      else onOverlaySelect?.(sourceIndex);
    },
    [onSubtitleSelect, onOverlaySelect]
  );

  // --- Ruler ticks ---

  const tickInterval = useMemo(() => {
    if (pxPerSecond > 60) return 1;
    if (pxPerSecond > 25) return 2;
    if (pxPerSecond > 12) return 5;
    return 10;
  }, [pxPerSecond]);

  const ticks = useMemo(() => {
    const result: number[] = [];
    for (let t = 0; t <= safeDuration; t += tickInterval) {
      result.push(t);
    }
    return result;
  }, [safeDuration, tickInterval]);

  // --- Visible tracks ---

  const visibleGroups = trackGroups.filter((g) => !hiddenTracks.has(g.id));

  const mediaTracksHeight = MEDIA_TRACK_HEIGHT * 2; // video + audio

  const totalTrackHeight =
    mediaTracksHeight +
    visibleGroups.reduce((sum, g) => {
      if (collapsedTracks.has(g.id)) return sum + 24;
      return sum + TRACK_HEIGHT;
    }, 0);

  const playheadLeft = currentTime * pxPerSecond;

  return (
    <div className="rounded-xl border border-white/[0.08] bg-black/50 overflow-hidden select-none flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/[0.06] bg-white/[0.02]">
        <span className="text-[11px] text-white/40 font-medium uppercase tracking-wider">
          Timeline
        </span>
        <div className="flex-1" />

        {/* Delete selected item */}
        {(selectedSubtitleIndex != null || selectedOverlayIndex != null) && (
          <button
            onClick={() => {
              if (selectedSubtitleIndex != null) onSubtitleDelete?.(selectedSubtitleIndex);
              else if (selectedOverlayIndex != null) onOverlayDelete?.(selectedOverlayIndex);
            }}
            className="flex items-center gap-1 px-1.5 py-1 rounded text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition"
            title="Usuń zaznaczony element"
          >
            <Trash2 size={11} />
            <span className="text-[10px]">Usuń</span>
          </button>
        )}

        {/* Split at playhead */}
        {(selectedSubtitleIndex != null || selectedOverlayIndex != null) && (
          <button
            onClick={() => {
              if (selectedSubtitleIndex != null) {
                const sub = subtitles[selectedSubtitleIndex];
                if (sub && currentTime > sub.startTime && currentTime < sub.endTime) {
                  onSubtitleSplit?.(selectedSubtitleIndex, currentTime);
                }
              } else if (selectedOverlayIndex != null) {
                const overlay = overlays[selectedOverlayIndex];
                if (overlay && currentTime > overlay.startTime && currentTime < overlay.endTime) {
                  onOverlaySplit?.(selectedOverlayIndex, currentTime);
                }
              }
            }}
            className="flex items-center gap-1 px-1.5 py-1 rounded text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 transition"
            title="Podziel w miejscu playheadu"
          >
            <Scissors size={11} />
            <span className="text-[10px]">Przytnij</span>
          </button>
        )}

        {(selectedSubtitleIndex != null || selectedOverlayIndex != null) && (
          <div className="w-px h-4 bg-white/[0.08] mx-1" />
        )}

        <span className="text-[10px] text-white/30 font-mono">
          {formatTime(currentTime)} / {formatTimeShort(safeDuration)}
        </span>
        <div className="w-px h-4 bg-white/[0.08] mx-1" />
        <button
          onClick={zoomOut}
          className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70 transition"
          title="Oddal"
        >
          <ZoomOut size={13} />
        </button>
        <div className="w-12 h-1 bg-white/[0.08] rounded-full relative">
          <div
            className="absolute top-0 h-1 bg-white/20 rounded-full"
            style={{ width: `${((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100}%` }}
          />
        </div>
        <button
          onClick={zoomIn}
          className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70 transition"
          title="Przybliż"
        >
          <ZoomIn size={13} />
        </button>
      </div>

      {/* Timeline body */}
      <div className="flex max-h-[260px] overflow-y-auto">
        {/* Track labels (fixed) */}
        <div
          className="shrink-0 border-r border-white/[0.06] bg-black/40 z-10"
          style={{ width: TRACK_LABEL_WIDTH }}
        >
          {/* Ruler spacer */}
          <div style={{ height: RULER_HEIGHT }} className="border-b border-white/[0.06]" />

          {/* Media track labels */}
          <div
            className="border-b border-white/[0.04] flex items-center gap-1 px-2"
            style={{ height: MEDIA_TRACK_HEIGHT }}
          >
            <span className="text-[11px] leading-none">🎥</span>
            <span className="text-[10px] text-white/50 truncate flex-1">Wideo</span>
          </div>
          <div
            className="border-b border-white/[0.04] flex items-center gap-1 px-2"
            style={{ height: MEDIA_TRACK_HEIGHT }}
          >
            <span className="text-[11px] leading-none">🔊</span>
            <span className="text-[10px] text-white/50 truncate flex-1">Audio</span>
          </div>

          {/* Track labels */}
          {visibleGroups.map((group) => {
            const isCollapsed = collapsedTracks.has(group.id);
            return (
              <div
                key={group.id}
                className="border-b border-white/[0.04] flex items-center gap-1 px-2"
                style={{ height: isCollapsed ? 24 : TRACK_HEIGHT }}
              >
                <button
                  onClick={() => toggleCollapse(group.id)}
                  className="p-0.5 text-white/25 hover:text-white/50 transition"
                >
                  {isCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
                </button>
                <span className="text-[11px] leading-none">{group.icon}</span>
                <span className="text-[10px] text-white/50 truncate flex-1">{group.label}</span>
                <span className="text-[9px] text-white/20">{group.items.length}</span>
                <button
                  onClick={() => toggleVisibility(group.id)}
                  className="p-0.5 text-white/20 hover:text-white/40 transition"
                  title={hiddenTracks.has(group.id) ? "Pokaż" : "Ukryj"}
                >
                  <Eye size={9} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Scrollable timeline area */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden"
          style={{
            cursor: dragState
              ? dragState.handle === "move"
                ? "grabbing"
                : "col-resize"
              : "default",
          }}
        >
          <div
            className="relative"
            style={{ width: totalWidth, height: RULER_HEIGHT + totalTrackHeight + 2 }}
          >
            {/* Playhead */}
            <div
              className="absolute top-0 bottom-0 z-30 pointer-events-none"
              style={{ left: playheadLeft }}
            >
              <div className="w-0.5 h-full bg-red-500/80" />
              <div className="absolute -top-px -left-[5px] w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-red-500" />
            </div>

            {/* Time ruler */}
            <div
              className="relative border-b border-white/[0.06] cursor-crosshair"
              style={{ height: RULER_HEIGHT }}
              onClick={handleRulerClick}
            >
              {ticks.map((t) => (
                <div
                  key={t}
                  className="absolute top-0 flex flex-col items-center"
                  style={{ left: t * pxPerSecond }}
                >
                  <div className="w-px h-3 bg-white/15" />
                  <span className="text-[8px] text-white/25 font-mono leading-none mt-0.5">
                    {formatTimeShort(t)}
                  </span>
                </div>
              ))}
              {/* Sub-ticks */}
              {pxPerSecond > 15 &&
                Array.from({ length: Math.ceil(safeDuration) }, (_, i) => i).map((t) => {
                  if (t % tickInterval === 0) return null;
                  return (
                    <div
                      key={`sub-${t}`}
                      className="absolute top-0 w-px h-1.5 bg-white/[0.07]"
                      style={{ left: t * pxPerSecond }}
                    />
                  );
                })}
            </div>

            {/* Media tracks — Video */}
            <div
              className="relative border-b border-white/[0.04]"
              style={{ height: MEDIA_TRACK_HEIGHT }}
              onClick={handleRulerClick}
            >
              <div
                className="absolute inset-0"
                style={{ backgroundColor: `${VIDEO_TRACK_COLOR}06` }}
              />
              <div
                className="absolute top-[4px] bottom-[4px] rounded-md flex items-center overflow-hidden"
                style={{
                  left: 0,
                  width: safeDuration * pxPerSecond,
                  backgroundColor: `${VIDEO_TRACK_COLOR}20`,
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: `${VIDEO_TRACK_COLOR}40`,
                }}
              >
                {/* Film frame pattern */}
                <div className="flex items-center h-full w-full overflow-hidden">
                  {Array.from(
                    { length: Math.min(600, Math.max(1, Math.ceil(safeDuration / 2))) },
                    (_, i) => (
                      <div
                        key={i}
                        className="shrink-0 h-full border-r border-dashed flex items-center justify-center"
                        style={{
                          width: 2 * pxPerSecond,
                          borderColor: `${VIDEO_TRACK_COLOR}15`,
                        }}
                      >
                        {i === 0 && (
                          <span className="text-[8px] text-green-400/60 font-medium truncate px-1">
                            {fileName ?? "video"}
                          </span>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Media tracks — Audio */}
            <div
              className="relative border-b border-white/[0.04]"
              style={{ height: MEDIA_TRACK_HEIGHT }}
              onClick={handleRulerClick}
            >
              <div
                className="absolute inset-0"
                style={{ backgroundColor: `${AUDIO_TRACK_COLOR}06` }}
              />
              <div
                className="absolute top-[4px] bottom-[4px] rounded-md flex items-center overflow-hidden"
                style={{
                  left: 0,
                  width: safeDuration * pxPerSecond,
                  backgroundColor: `${AUDIO_TRACK_COLOR}18`,
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: `${AUDIO_TRACK_COLOR}35`,
                }}
              >
                {/* Waveform visualization */}
                <div className="flex items-center h-full w-full px-0.5 gap-[1px]">
                  {Array.from(
                    { length: Math.max(1, Math.min(200, Math.ceil(safeDuration * 4))) },
                    (_, i) => {
                      const height =
                        20 +
                        Math.sin(i * 0.8) * 30 +
                        Math.sin(i * 2.1) * 20 +
                        Math.cos(i * 0.3) * 15;
                      return (
                        <div
                          key={i}
                          className="flex-1 min-w-[1px] rounded-full"
                          style={{
                            height: `${Math.max(8, Math.min(90, height))}%`,
                            backgroundColor: `${AUDIO_TRACK_COLOR}50`,
                          }}
                        />
                      );
                    }
                  )}
                </div>
              </div>
            </div>

            {/* Editable tracks */}
            {visibleGroups.map((group) => {
              const isCollapsed = collapsedTracks.has(group.id);
              const trackH = isCollapsed ? 24 : TRACK_HEIGHT;

              return (
                <div
                  key={group.id}
                  className="relative border-b border-white/[0.04]"
                  style={{ height: trackH }}
                  onClick={handleRulerClick}
                >
                  {/* Track background stripe */}
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: `${group.color}04` }}
                  />

                  {/* Items */}
                  {!isCollapsed &&
                    group.items.map((item) => {
                      const left = item.startTime * pxPerSecond;
                      const width = Math.max(
                        DRAG_HANDLE_WIDTH * 3,
                        (item.endTime - item.startTime) * pxPerSecond
                      );
                      const isSelected =
                        (item.sourceType === "subtitle" &&
                          selectedSubtitleIndex === item.sourceIndex) ||
                        (item.sourceType === "overlay" &&
                          selectedOverlayIndex === item.sourceIndex);
                      const isDragging =
                        dragState?.type === item.sourceType &&
                        dragState?.index === item.sourceIndex;

                      return (
                        <div
                          key={`${item.sourceType}-${item.sourceIndex}`}
                          className={`
                          absolute top-[5px] bottom-[5px] rounded-md flex items-center overflow-hidden
                          transition-shadow
                          ${isSelected ? "ring-1 ring-white/40 shadow-lg" : ""}
                          ${isDragging ? "opacity-90" : ""}
                        `}
                          style={{
                            left,
                            width,
                            backgroundColor: `${group.color}${isSelected ? "45" : "25"}`,
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderColor: `${group.color}${isSelected ? "80" : "50"}`,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleItemClick(item.sourceType, item.sourceIndex);
                          }}
                        >
                          {/* Left drag handle */}
                          <div
                            className="absolute left-0 top-0 bottom-0 cursor-col-resize z-10 flex items-center"
                            style={{ width: DRAG_HANDLE_WIDTH }}
                            onMouseDown={(e) =>
                              startDrag(
                                e,
                                item.sourceType,
                                item.sourceIndex,
                                "left",
                                item.startTime,
                                item.endTime
                              )
                            }
                          >
                            <div
                              className="w-[2px] h-3 rounded-full ml-[2px] opacity-0 hover:opacity-100 transition-opacity"
                              style={{ backgroundColor: group.color }}
                            />
                          </div>

                          {/* Content (draggable for move) */}
                          <div
                            className="flex-1 flex items-center gap-1 px-2 min-w-0 cursor-grab active:cursor-grabbing"
                            onMouseDown={(e) =>
                              startDrag(
                                e,
                                item.sourceType,
                                item.sourceIndex,
                                "move",
                                item.startTime,
                                item.endTime
                              )
                            }
                          >
                            <span
                              className="text-[9px] truncate leading-none font-medium"
                              style={{ color: `${group.color}cc` }}
                            >
                              {item.text || "—"}
                            </span>
                          </div>

                          {/* Right drag handle */}
                          <div
                            className="absolute right-0 top-0 bottom-0 cursor-col-resize z-10 flex items-center justify-end"
                            style={{ width: DRAG_HANDLE_WIDTH }}
                            onMouseDown={(e) =>
                              startDrag(
                                e,
                                item.sourceType,
                                item.sourceIndex,
                                "right",
                                item.startTime,
                                item.endTime
                              )
                            }
                          >
                            <div
                              className="w-[2px] h-3 rounded-full mr-[2px] opacity-0 hover:opacity-100 transition-opacity"
                              style={{ backgroundColor: group.color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
